import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale } from 'chart.js';
import loadingGif from '../../assets/loading.gif';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Box,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogAction
} from '@mui/material';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import { IconButton } from '@mui/material';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import Swal from 'sweetalert2';
import Select from 'react-select';
import API_BASE_URL from '../../config';
import './CreateJobFlow.css';
import CloseIcon from '@mui/icons-material/Close';

// Register required Chart.js elements and plugins
ChartJS.register(BarElement, CategoryScale, LinearScale, ChartDataLabels);


const CreateJobFlow = () => {
    const { orgId } = useParams();
    const [organization, setOrganization] = useState(null);
    const [jobFlowData, setJobFlowData] = useState({ maxJobFlow: 0, jobFlowCount: 0 });
    const [jobFlowName, setJobFlowName] = useState('New Job Flow');
    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState([
        { id: 1, stepName: 'Step 1', stepColor: '#8d99ae', allowedRoles: [], emailNotification: false, allowUpdate: false },
        { id: 2, stepName: 'Step 2', stepColor: '#f77f00', allowedRoles: [], emailNotification: false, allowUpdate: false },
        { id: 3, stepName: 'Step 3', stepColor: '#d62828', allowedRoles: [], emailNotification: false, allowUpdate: false },
        { id: 4, stepName: 'Completed', stepColor: '#2a9d8f', allowedRoles: [], emailNotification: false, allowUpdate: false }, // "Completed" step is always last
    ]);
    const [orgRole, setOrgRole] = useState([]);
    const [maxFormDefinatioNItems, setMaxFormDefinationItems] = useState(0);

    const [editFormOpen, setEditFormOpen] = useState(false);
    const [currentEditId, setCurrentEditId] = useState(null);
    const [formFields, setFormFields] = useState([]); // fields for the dialog
    const [newQuestionName, setNewQuestionName] = useState('');
    const [newQuestionType, setNewQuestionType] = useState('boolean');
    const [newDropdownOptions, setNewDropdownOptions] = useState('');


    const getContrastColor = (hexColor) => {
        // Remove '#' if present
        const color = hexColor.charAt(0) === '#' ? hexColor.substring(1) : hexColor;
        // Convert to RGB
        const r = parseInt(color.substr(0, 2), 16);
        const g = parseInt(color.substr(2, 2), 16);
        const b = parseInt(color.substr(4, 2), 16);
        // Calculate luminance
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        // Return black for light colors, white for dark colors
        return luminance > 0.5 ? 'black' : 'white';
    };

    
    const colorStyles = {
        option: (styles, { data, isFocused, isSelected }) => {
            const backgroundColor = isSelected ? data.value : isFocused ? data.value : 'white';
            const color = isSelected || isFocused ? getContrastColor(data.value) : 'black';
            return {
                ...styles,
                backgroundColor,
                color,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
            };
        },
        singleValue: (styles, { data }) => ({
            ...styles,
            backgroundColor: data.value,
            color: getContrastColor(data.value),
            padding: '2px 8px',
            borderRadius: 4,
            display: 'inline-block',
        }),
    };

    const ColourOption = (props) => {
        const { data, innerRef, innerProps, isFocused, isSelected } = props;
        const backgroundColor = isSelected ? data.value : isFocused ? data.value : 'white';
        const color = isSelected || isFocused ? getContrastColor(data.value) : 'black';

        return (
            <div
                ref={innerRef}
                {...innerProps}
                style={{
                    backgroundColor,
                    color,
                    padding: 8,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                }}
            >
                <div
                    style={{
                        width: 16,
                        height: 16,
                        backgroundColor: data.value,
                        marginRight: 8,
                        border: '1px solid #ccc',
                        borderRadius: 2,
                    }}
                />
                {data.label}
            </div>
        );
    };

    const colorOptions = [
        { value: '#d9d9d9', label: 'Light Gray' },
        { value: '#d1c842', label: 'Yellow' },
        { value: '#683b08', label: 'Brown' },
        { value: '#2b2b2b', label: 'Black' },
        { value: '#3e5570', label: 'Blue' },
        { value: '#e63946', label: 'Red' },
        { value: '#f4a261', label: 'Orange' },
        { value: '#2a9d8f', label: 'Teal' },
        { value: '#264653', label: 'Dark Blue' },
        { value: '#6a994e', label: 'Green' },
        { value: '#9a8c98', label: 'Purple' },
        { value: '#ffb703', label: 'Gold' },
        { value: '#8d99ae', label: 'Slate Gray' },
        { value: '#007f5f', label: 'Deep Green' },
        { value: '#d62828', label: 'Crimson' },
        { value: '#f77f00', label: 'Pumpkin' },
        { value: '#003049', label: 'Prussian Blue' },
        { value: '#6a0572', label: 'Dark Violet' },
    ];


    const navigate = useNavigate();
    const [warningMessage, setWarningMessage] = useState('');

    useEffect(() => {
        const fetchOrganizationDetails = async () => {
            setLoading(true);

            const token = localStorage.getItem('token');

            if (!token) {
                navigate('/unauthorized');
                return;
            }

            try {
                const orgResponse = await axios.get(`${API_BASE_URL}/org/organization-settings/organization-name`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { orgId },
                });

                if (orgResponse.status === 403) {
                    navigate('/unauthorized');
                    return;
                }
                setOrganization(orgResponse.data);

                const jobFlowResponse = await axios.get(`${API_BASE_URL}/org/organization-settings/job-flow-max-count`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { orgId },
                });

                if (jobFlowResponse.status === 403) {
                    navigate('/unauthorized');
                    return;
                }
                setJobFlowData(jobFlowResponse.data);
                
                const org_roles = await axios.get(`${API_BASE_URL}/org/organization-settings/roles/${orgId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (org_roles.status === 403) {
                    navigate('/unauthorized');
                    return;
                }
                // Transform org roles into an array of objects for react-select
                const roleOptions = org_roles.data.map(role => ({
                    value: role.role_id,
                    label: role.role_name
                }));

                setOrgRole(roleOptions);

                const formDefinationItemResponse = await axios.get(`${API_BASE_URL}/org/organization-settings/max-form-defination-item`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { orgId },
                });

                if (formDefinationItemResponse.status === 403) {
                    navigate('/unauthorized');
                    return;
                }
                setMaxFormDefinationItems(formDefinationItemResponse.data);
            } catch (error) {
                console.error('Error fetching data:', error);
                navigate('/unauthorized');
                return;
            } finally {
                setLoading(false);
            }
        };
        
        fetchOrganizationDetails();
    }, [orgId, navigate]);

    

    const handleCreateJobFlow = async (event) => {
        event.preventDefault();
    
        const isValid = tableData.slice(0, -1).every(row =>
            row.stepName.trim() !== '' && 
            row.stepColor.trim() !== '' && 
            row.allowedRoles.length > 0
        );
    
        if (!isValid) {
            Swal.fire('Validation Error', 'Please fill out all fields before submitting.', 'error');
            return;
        }
    
        if (jobFlowData.jobFlowCount >= jobFlowData.maxJobFlow) {
            Swal.fire('Limit Reached', 'You have reached the maximum number of job flows allowed.', 'error');
            return;
        }
        // Prepare the job flow data
        const newJobFlow = { 
            orgId,
            jobFlowName,
            steps: tableData.map(row => ({
                index: row.id,
                stepName: row.stepName,
                stepColor: row.stepColor,
                allowedRoles: row.allowedRoles,
                emailNotification: row.emailNotification,
                allowUpdate: row.allowUpdate,
                ...(row.allowUpdate && row.formDefinition && { formDefinition: row.formDefinition })
            })),
        };
    
        // Display confirmation dialog
        Swal.fire({
            title: 'Confirm Creation',
            text: 'Are you sure you want to create this job flow?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, create it!',
            cancelButtonText: 'No, cancel!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                const token = localStorage.getItem('token');
                try {
                    const response = await axios.post(`${API_BASE_URL}/org/organization-settings/job-flow/create`, newJobFlow, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
    
                    if (response.status === 201) {
                        const result = await Swal.fire('Success', 'Job flow created successfully!', 'success');
                        // Redirect if the user clicks "OK"
                        if (result.isConfirmed) {
                            navigate('/organization/settings');
                        }
                    } else {
                        Swal.fire('Error', response.data.message || 'Failed to create job flow.', 'error');
                    }
                } catch (error) {
                    console.error('Error creating job flow:', error);
                    Swal.fire('Error', error.response?.data?.message || 'Failed to create job flow.', 'error');
                }
            }
        });
    };    

    const calculateUsagePercentage = () => {
        if (jobFlowData) {
        return Math.floor((jobFlowData.jobFlowCount / jobFlowData.maxJobFlow) * 100);
        }
        return 0;
    };
        
    

    const handleAddRow = () => {
        if (tableData.length >= 20) {
            setWarningMessage('Limit reached. Cannot create over 20 steps!');
            return;
        }
    
        // Create a new step
        const newStepId = Math.max(...tableData.map(row => row.id), 0) + 1;
        const newRow = {
            id: newStepId, // New ID for the new step
            stepName: `Step ${newStepId - 1}`,
            stepColor: '#8d99ae',
            allowedRoles: [],
            emailNotification: false,
            allowUpdate: false
        };
    
        // Update the table data
        setTableData(prevData => {
            const newData = [...prevData]; // Create a copy of the existing data
    
            // Insert the new row before the "Completed" step (which is always the last item)
            const completedIndex = newData.length - 1; // Get the index of "Completed" step
            newData.splice(completedIndex, 0, newRow); // Insert the new row
    
            // Increment the "Completed" step's index
            newData[completedIndex + 1] = { 
                ...newData[completedIndex + 1], 
                id: newData[completedIndex + 1].id + 1 // Increment the ID of the "Completed" step
            };
    
            // Remap IDs to be sequential again
            return newData.map((row, index) => ({
                ...row,
                id: index + 1 // Reassign IDs starting from 1
            }));
        });
    
        setWarningMessage(''); // Reset warning message on add row
    };

    const handleStepNameChange = (id, field, value) => {
        if (field === 'stepName' && value.length > 30) {
            setWarningMessage('Step Name must not exceed 30 characters.');
            return;
        }
    
        // Update only if the step is not "Completed"
        if (field === 'stepName' && id !== tableData[tableData.length - 1].id) {
            setTableData(tableData.map(row => {
                if (row.id === id) {
                    const updatedRow = { ...row, [field]: value };
                    return updatedRow;
                }
                return row;
            }));
            setWarningMessage('');
        }
    };

    const handleEmailNotificationChange = (id, checked) => {
        setTableData(tableData.map(row => {
            if (row.id === id) {
                return { ...row, emailNotification: checked };
            }
            return row;
        }));
    };
    
    const handleCreateForm = (id, checked) => {
        if (!checked) {
            setTableData(tableData.map(row => 
                row.id === id ? { ...row, allowUpdate: false, formDefinition: undefined } : row
            ));
            return;
        }

        const step = tableData.find(row => row.id === id);
        if (!step) {
            console.error('Step not found');
            return;
        }
        setCurrentEditId(id);
        setFormFields(step.formDefinition ? step.formDefinition.fields : []);
        setNewQuestionName('');
        setNewQuestionType('boolean');
        setNewDropdownOptions('');
        setEditFormOpen(true);
    };

    const handleEditForm = (id) => {
        const step = tableData.find(row => row.id === id);
        if (!step) {
            console.error('Step not found');
            return;
        }
        setCurrentEditId(id);
        setFormFields(step.formDefinition ? step.formDefinition.fields : []);
        setNewQuestionName('');
        setNewQuestionType('boolean');
        setNewDropdownOptions('');
        setEditFormOpen(true);
    };

    const handleAddQuestion = () => {
        if (!newQuestionName.trim()) {
            alert('Please enter a question name');
            return;
        }
        if (newQuestionType === 'dropdown' && !newDropdownOptions.trim()) {
            alert('Please enter options for the dropdown');
            return;
        }
        if (formFields.length >= maxFormDefinatioNItems) {
            alert(`Cannot add more than ${maxFormDefinatioNItems} questions.`);
            return;
        }
        const newField = {
            name: newQuestionName.toLowerCase().replace(/\s+/g, '_'),
            label: newQuestionName,
            type: newQuestionType,
            options: newQuestionType === 'dropdown' ? newDropdownOptions.split(',').map(opt => opt.trim()) : undefined,
        };
        setFormFields([...formFields, newField]);
        setNewQuestionName('');
        setNewDropdownOptions('');
        setNewQuestionType('boolean');
    };

    const handleDeleteQuestion = (index) => {
        setFormFields(formFields.filter((_, i) => i !== index));
    };

    const handleSaveForm = () => {
        setTableData(tableData.map(row => 
            row.id === currentEditId ? { ...row, allowUpdate: true, formDefinition: { fields: formFields } } : row
        ));
        setEditFormOpen(false);
        Swal.fire({
            title: 'Form Saved',
            text: 'The form has been updated successfully.',
            icon: 'success',
            confirmButtonText: 'Close',
            allowOutsideClick: false,
            allowEscapeKey: false
        });
    };

    const handleCancelEdit = () => {
        setEditFormOpen(false);
    };

    
    

    const handleDeleteRow = (id) => {
        if (id === tableData[tableData.length - 1].id) {
            Swal.fire('Cannot delete', 'The "Completed" step cannot be deleted.', 'error');
            return;
        }
    
        Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
        }).then((result) => {
            if (result.isConfirmed) {
                setTableData(prevData => {
                    const newData = prevData.filter(row => row.id !== id);
                    // Remap IDs to be sequential after deletion
                    return newData.map((row, index) => ({ ...row, id: index + 1 }));
                });
                setWarningMessage(''); // Clear the warning message on delete
                Swal.fire('Deleted!', 'Your row has been deleted.', 'success');
            }
        });
    };
    

    const handleColorChange = (id, color) => {
        setTableData(tableData.map(row => (row.id === id ? { ...row, stepColor: color } : row)));
    };

    const handleRoleChange = (id, selectedOptions) => {
        // Update the allowed roles for the row
        const selectedRoles = selectedOptions ? selectedOptions.map(option => option.value) : [];
        setTableData(tableData.map(row => (row.id === id ? { ...row, allowedRoles: selectedRoles } : row)));
    };

    if (loading) {
        return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100vh - 64px)',
            backgroundColor: '#fff',
        }}>
            <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
        </div>
        );
    }

    const progressPercentage = Math.round(Math.min((jobFlowData.jobFlowCount / jobFlowData.maxJobFlow) * 100, 100));
    const isCreateButtonEnabled = jobFlowData && jobFlowData.jobFlowCount < jobFlowData.maxJobFlow;
    
    const handleBarClick = (elements) => {
        if (elements.length > 0) {
            const index = elements[0].index + 1;
            const step = tableData[index];
        }
    };

    const getRandomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

    const chartData = {
        labels: tableData.map(row => row.stepName),
        datasets: [
            {
                label: 'Job Flow Steps',
                data: tableData.map(() => getRandomInt(10, 100)),
                backgroundColor: tableData.map(row => row.stepColor || '#3e5570'),
                borderColor: tableData.map(row => row.stepColor ? 'rgba(0, 0, 0, 0.5)' : '#000'),
                borderWidth: 0,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
        y: {
        display: false,
        grid: {
            display: false,
            drawBorder: false,
        },
        min: 0, // Ensure bars start at zero
        },
        x: {
        grid: { display: false },
        ticks: { color: '#3e5570', font: { size: 14, family: 'Roboto, Arial' }, maxRotation: 45, minRotation: 0 },
        border: { display: false },
        },
    },
        plugins: {
        legend: { display: false },
        tooltip: {
            enabled: true,
            backgroundColor: '#3e5570',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: '#d1c842',
            borderWidth: 2,
            callbacks: {
            label: (tooltipItem) => `${tooltipItem.label}: ${tooltipItem.parsed.y}`,
            },
        },
        datalabels: {
            display: true,
            anchor: 'end',
            align: 'end',
            color: '#3e5570',
            font: { size: 16, weight: 'bold' },
            formatter: (value) => value,
        },
        },
        animation: {
        duration: 1000,
        easing: 'easeOutCubic',
        },
        bar: {
            borderRadius: 10,
            maxBarThickness: 36,
        },
        layout: {
            padding: { top: 36, left: 12, right: 12, bottom: 12 },
        },
            onClick: (event, elements) => handleBarClick(elements),
        };
    
    return (
        <>
            <Card>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 0 }}>
                    <IconButton
                        onClick={() => navigate('/organization/settings')}
                        aria-label="Back"
                        sx={{
                            border: '1px solid black',
                            backgroundColor: 'transparent',
                            color: 'black',
                            margin: 2,
                            height: 36,
                            width: 36,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                    <KeyboardReturnRoundedIcon />
                    </IconButton>
                </Box>
                <CardContent>
                    <Grid container spacing={3} mb={2}>
                        <Grid item xs={12} md={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>{organization.orgName}: Create Job Flow</Typography>
                                    {jobFlowData != null && (
                                        <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Current Plan Usage: {jobFlowData.jobFlowCount} / {jobFlowData.maxJobFlow} Organization(s)
                                        </Typography>
                                        <LinearProgress variant="determinate" value={calculateUsagePercentage()} sx={{ height: 8, borderRadius: 4, mt: 1 }} />
                                        </Box>
                                    )}
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={1}
                                            size='small'
                                            label="Job Flow Name*"
                                            value={jobFlowName}
                                            onChange={(e) => setJobFlowName(e.target.value)}
                                        />
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Job Flow Statistics
                                    </Typography>
                                    <Box sx={{ height: '30vh', position: 'relative' }}>
                                        <Bar data={chartData} options={{
                                            ...chartOptions,
                                            maintainAspectRatio: false,
                                            responsive: true
                                        }} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                    <form onSubmit={handleCreateJobFlow}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                            <TableContainer component={Paper}>
                                <Table>
                                <TableHead>
                                    <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Step Name</TableCell>
                                    <TableCell>Color</TableCell>
                                    <TableCell>Access</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Form</TableCell>
                                    <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableData.map((row, index) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>
                                            <TextField
                                                value={row.stepName}
                                                onChange={(e) => handleStepNameChange(row.id, 'stepName', e.target.value)}
                                                disabled={row.stepName === 'Completed'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Select
                                                options={colorOptions}
                                                value={colorOptions.find(option => option.value === row.stepColor)}
                                                onChange={(selectedOption) => handleColorChange(row.id, selectedOption.value)}
                                                isSearchable={false}
                                                styles={colorStyles}
                                                components={{ Option: ColourOption }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 500 }}>
                                        <Select
                                            isMulti
                                            options={orgRole}
                                            value={row.allowedRoles.map(roleId => orgRole.find(role => role.value === roleId))}
                                            onChange={(selectedOptions) => handleRoleChange(row.id, selectedOptions)}
                                            closeMenuOnSelect={false}
                                        />
                                        </TableCell>
                                        <TableCell>
                                        <FormControlLabel
                                            control={
                                            <Switch
                                                checked={row.emailNotification}
                                                onChange={(e) => handleEmailNotificationChange(row.id, e.target.checked)}
                                            />
                                            }
                                        />
                                        </TableCell>
                                        <TableCell>
                                        <FormControlLabel
                                            control={
                                            <Switch
                                                checked={row.allowUpdate}
                                                onChange={(e) => handleCreateForm(row.id, e.target.checked)}
                                            />
                                            }
                                        />
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                disabled={!row.allowUpdate}
                                                onClick={(e) => {
                                                    e.preventDefault(); // Prevent form submission
                                                    handleEditForm(row.id);
                                                }}
                                                sx={{ mr: 1 }}
                                            >
                                                Edit Form
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={(e) => {
                                                    e.preventDefault(); // Prevent form submission
                                                    handleDeleteRow(row.id);
                                                }}
                                                disabled={row.id === tableData[tableData.length - 1].id}
                                                sx={{ display: row.id === tableData[tableData.length - 1].id ? 'none' : 'inline-flex' }}
                                            >
                                                Delete
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell colSpan={7}>
                                            <Button variant="contained" color="primary" onClick={handleAddRow}>
                                                Add Step
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                                </Table>
                            </TableContainer>
                            </Grid>
                            <Grid item xs={12}>
                            <Typography color="error">{warningMessage}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                            <Button type="submit" variant="contained" color="primary">
                                Create Job Flow
                            </Button>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>

            <Dialog open={editFormOpen} onClose={handleCancelEdit} maxWidth="md" fullWidth>
                <DialogTitle sx={{ color: 'black' }}>
                    Edit Form
                    <IconButton
                        aria-label="close"
                        onClick={handleCancelEdit}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ border: '1px solid #ddd', padding: 12, width: '20%' }}>Name</th>
                                <th style={{ border: '1px solid #ddd', padding: 12, width: '20%' }}>Type</th>
                                <th style={{ border: '1px solid #ddd', padding: 12, width: '40%' }}>Dropdown Options</th>
                                <th style={{ border: '1px solid #ddd', padding: 12, width: '20%' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {formFields.map((field, index) => (
                                <tr key={index}>
                                    <td style={{ border: '1px solid #ddd', padding: 12 }}>{field.label}</td>
                                    <td style={{ border: '1px solid #ddd', padding: 12 }}>{field.type}</td>
                                    <td style={{ border: '1px solid #ddd', padding: 12 }}>{field.options ? field.options.join(', ') : ''}</td>
                                    <td style={{ border: '1px solid #ddd', padding: 12 }}>
                                        <Button variant="contained" color="error" size="small" onClick={() => handleDeleteQuestion(index)}>
                                            Delete
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            <tr>
                                <td style={{ border: '1px solid #ddd', padding: 12 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Question Name"
                                        value={newQuestionName}
                                        onChange={(e) => setNewQuestionName(e.target.value)}
                                    />
                                </td>
                                <td style={{ border: '1px solid #ddd', padding: 12 }}>
                                    <select
                                        value={newQuestionType}
                                        onChange={(e) => setNewQuestionType(e.target.value)}
                                        style={{ width: '100%', padding: 8 }}
                                    >
                                        <option value="boolean">boolean</option>
                                        <option value="dropdown">dropdown</option>
                                        <option value="text">text</option>
                                        <option value="datetime">datetime</option>
                                    </select>
                                </td>
                                <td style={{ border: '1px solid #ddd', padding: 12 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Comma-separated options"
                                        value={newDropdownOptions}
                                        onChange={(e) => setNewDropdownOptions(e.target.value)}
                                        disabled={newQuestionType !== 'dropdown'}
                                    />
                                </td>
                                <td style={{ border: '1px solid #ddd', padding: 12 }}>
                                    <Button variant="contained" onClick={handleAddQuestion}>
                                        Add Question
                                    </Button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelEdit}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveForm} disabled={formFields.length === 0}>
                        Save Form
                    </Button>
                </DialogActions>
            </Dialog>

        
        </>
    );
};

export default CreateJobFlow;
